'use client';

import { useExploreSection } from '@/lib/hooks/useSiteData';
import Link from 'next/link';
import { useParams } from 'next/navigation';

const ExploreSection = () => {
  const params = useParams();
  const currentLang = (params?.lang as string) || 'en';
  const { data: exploreData, loading, error } = useExploreSection();

  // Helper function to decode base64 content
  const decodeContent = (base64Content: string) => {
    try {
      return atob(base64Content);
    } catch (error) {
      console.error('Error decoding base64 content:', error);
      return '';
    }
  };

  // Helper function to extract text from HTML content
  const extractTextFromHtml = (htmlContent: string) => {
    // Remove HTML tags and extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Show loading state
  if (loading) {
    return (
      <section 
        className="explore-sec"
        style={{
          backgroundImage: 'url(/images/banner/explore_bg.png)',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="container">
          <div className="row align-items-center justify-content-between">
            <div className="col-lg-6">
              <div className="content-box">
                <div className="placeholder-glow">
                  <h2 className="placeholder col-10 mb-3"></h2>
                  <p className="placeholder col-12 mb-2"></p>
                  <p className="placeholder col-8 mb-3"></p>
                  <div className="placeholder" style={{ width: '150px', height: '40px', borderRadius: '20px' }}></div>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="explore-img">
                <div className="placeholder" style={{ width: '100%', height: '300px', borderRadius: '8px' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section 
        className="explore-sec"
        style={{
          backgroundImage: 'url(/images/banner/explore_bg.png)',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="container">
          <div className="row align-items-center justify-content-between">
            <div className="col-lg-6">
              <div className="content-box">
                <h2>Explore the Caregiver Tech Tool Finder</h2>
                <p>
                  The Caregiver Tech Tool Finder provides a curated list of the best apps, websites, wearables
                  and other tech tools for caregivers.
                </p>
                <a href="/page/caregiver-tech-tool-finder" className="btn btn-secondary">
                  <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                  Learn More
                </a>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="explore-img">
                <img src="/images/boxes/explore_img.png" alt="Caregiver Tech Tool Finder" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Helper to slugify text for URLs
  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Helper to get link or generate default
  const getLinkOrDefault = (link: string | null | undefined, label: string) => {
    if (link && link !== '' && link !== '#' && link !== '/') {
      return link;
    }
    return `/${currentLang}/page/${slugify(label)}`;
  };

  // Process API data
  const title = exploreData?.data?.title || 'Explore the Caregiver Tech Tool Finder';
  const content = exploreData?.data?.content || '';
  const buttonText = exploreData?.data?.specifications?.find((spec: any) => spec.key === 'button-text')?.value || 'Learn More';
  const buttonLink = exploreData?.data?.specifications?.find((spec: any) => spec.key === 'button-link')?.value || '#';
  const imageUrl = exploreData?.data?.specifications?.find((spec: any) => spec.key === 'image')?.value || '/images/boxes/explore_img.png';

  // Decode and extract text from HTML content
  const decodedContent = decodeContent(content);
  const textContent = extractTextFromHtml(decodedContent);
  
  // Extract description from the decoded content (remove title if present)
  const description = textContent.replace(/^.*?Explore the Caregiver Tech Tool Finder\s*/, '').trim() || 
    'The Caregiver Tech Tool Finder provides a curated list of the best apps, websites, wearables and other tech tools for caregivers.';

  return (
    <section 
      className="explore-sec"
      style={{
        backgroundImage: 'url(/images/banner/explore_bg.png)',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="container">
        <div className="row align-items-center justify-content-between">
          <div className="col-lg-6">
            <div className="content-box">
              <h2>{title}</h2>
              <p>{description}</p>
              <Link href={getLinkOrDefault(buttonLink, title)} className="btn btn-secondary">
                <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                {buttonText}
              </Link>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="explore-img">
              <img 
                src={imageUrl} 
                alt="Caregiver Tech Tool Finder"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/boxes/explore_img.png";
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExploreSection;
